import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import { useState } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faHouse, faKey } from "@fortawesome/free-solid-svg-icons";

export default function Evaluation() {
  const [popup, setPopup] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    propertyAddress: "",
    city: "",
    provinceState: "",
    propertySize: "",
    lotSize: "",
    propertyType: "",
    buildingType: "",
    notes: "",
  });

  const seoConfig = pageSEO.evaluation();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  async function handleSubmit(event) {
    event.preventDefault();

    const submissionData = {
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Lead Interested in Home Evaluation",
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      const data = await res.json();

      setPopup(
        data?.success
          ? {
            subject: "Thank You!",
            message:
              "Your evaluation request has been received. We will contact you shortly.",
          }
          : {
            subject: "Something went wrong",
            message: "Please try again later.",
          }
      );
    } catch {
      setPopup({
        subject: "Something went wrong",
        message: "Please try again later.",
      });
    }
  }

  return (
    <>
      <SEO {...seoConfig} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#fffdfa] pt-40 pb-12 lg:pb-28">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mx-auto max-w-[1200px] px-6 lg:px-16"
        >
          <motion.div
            variants={fadeUp}
            className="flex items-center justify-center gap-5 mb-8"
          >
            <div className="h-px w-16 bg-[#8b8172]" />
            <span className="text-xs text-center uppercase tracking-[0.45em] text-[#6d655b]">
              Complimentary Service
            </span>

            <div className="h-px w-16 bg-[#8b8172]" />
          </motion.div>

          <div className="text-center">
            <motion.h1
              variants={fadeUp}
              className="font-serif text-5xl leading-[1.05] text-[#2b2925] lg:text-7xl"
            >
              Discover Your
              <br />
              Home's True Value
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mx-auto mt-10 max-w-3xl text-lg leading-9 text-[#5f594f]"
            >
              Receive a personalized property evaluation based on current market
              trends, comparable homes, and expert local knowledge.
            </motion.p>
          </div>
        </motion.div>
      </section>

      {/* CONTENT */}
      <section className="bg-[#fffdfa] pb-32">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10 lg:px-16">

          <div className="grid gap-8 lg:grid-cols-[450px_1fr]">
            {/* IMAGE */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="block"
            >
              <div className="sticky top-32">
                <div className="relative">
                  {/* Frame */}
                  <div className="absolute -left-9 -top-9 h-full w-full border-[15px] border-[#8b8172]/40 hidden lg:block" />

                  <img
                    src={house}
                    alt="Home Evaluation"
                    className="relative h-[450px] lg:h-[650px] w-full object-cover"
                  />

                  {/* Label */}
                  <div className="absolute bottom-0 left-0 bg-[#d3d2d0] px-8 py-6">
                    <p className="mt-2 text-xs uppercase tracking-[0.35em] text-[#6d655b]">
                      Home Evaluation
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* FORM */}

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="border border-[#d8d0c6] bg-white px-4 py-8 md:p-12">
                <div className="mb-10">
                  <p className="text-xs uppercase tracking-[0.4em] text-[#8b8172]">
                    Property Analysis
                  </p>

                  <h2 className="mt-5 font-serif text-4xl text-[#2b2925]">
                    Request Your Evaluation
                  </h2>

                  <p className="mt-4 leading-8 text-[#5f594f]">
                    Complete the form below and receive your complimentary home
                    evaluation with professional market insights.
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <input
                    required
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full Name"
                    className={`w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none transition focus:border-[#8b8172]`}
                  />

                  <div className="grid gap-6 md:grid-cols-2">
                    <input
                      required
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email Address"
                      className={`w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none focus:border-[#8b8172]`}
                    />

                    <input
                      required
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Phone Number"
                      className={`w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none focus:border-[#8b8172]`}
                    />
                  </div>

                  <input
                    required
                    name="propertyAddress"
                    value={formData.propertyAddress}
                    onChange={handleInputChange}
                    placeholder="Property Address"
                    className={`w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none focus:border-[#8b8172]`}
                  />

                  <div className="grid gap-6 md:grid-cols-2">
                    <input
                      required
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="City"
                      className={`w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none focus:border-[#8b8172]`}
                    />

                    <input
                      required
                      name="provinceState"
                      value={formData.provinceState}
                      onChange={handleInputChange}
                      placeholder="Province / State"
                      className={`w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none focus:border-[#8b8172]`}
                    />
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    <input
                      required
                      name="propertySize"
                      value={formData.propertySize}
                      onChange={handleInputChange}
                      placeholder="Property Size"
                      className={`w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none focus:border-[#8b8172]`}
                    />

                    <input
                      required
                      name="lotSize"
                      value={formData.lotSize}
                      onChange={handleInputChange}
                      placeholder="Lot Size"
                      className={`w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none focus:border-[#8b8172]`}
                    />
                  </div>

                  <select
                    required
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleInputChange}
                    className={`w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none focus:border-[#8b8172]`}
                  >
                    <option value="">
                      Property Type
                    </option>

                    <option>
                      Residential
                    </option>

                    <option>
                      Commercial
                    </option>

                    <option>
                      Land
                    </option>

                    <option>
                      Investment
                    </option>
                  </select>

                  <select
                    required
                    name="buildingType"
                    value={formData.buildingType}
                    onChange={handleInputChange}
                    className={`w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none focus:border-[#8b8172]`}
                  >
                    <option value="">
                      Building Type
                    </option>

                    <option>
                      House
                    </option>

                    <option>
                      Townhouse
                    </option>

                    <option>
                      Condo
                    </option>

                    <option>
                      Duplex
                    </option>
                  </select>

                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Additional Information"
                    className={`w-full border border-[#d8d0c6] bg-[#fffdfa] px-5 py-4 text-[#2b2925] outline-none focus:border-[#8b8172] h-32`}
                  />





                  <button
                    className="
                  mt-4
                  border
                  border-[#2b2925]
                  px-10
                  py-4
                  text-xs
                  uppercase
                  tracking-[0.35em]
                  text-[#2b2925]
                  transition-all
                  duration-300
                  hover:bg-[#2b2925]
                  hover:text-[#fffdfa]
                "
                  >

                    Request Evaluation

                  </button>



                </form>


              </div>


            </motion.div>



          </div>


        </div>


      </section>





      {/* POPUP */}

      {popup && (
        <>

          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />


          <div className="
        fixed
        left-1/2
        top-1/2
        z-50
        w-[90%]
        max-w-md
        -translate-x-1/2
        -translate-y-1/2
        bg-white
        p-10
        text-center
        shadow-2xl
      ">


            <h3 className="font-serif text-3xl text-[#2b2925]">
              {popup.subject}
            </h3>


            <p className="mt-4 leading-8 text-[#5f594f]">
              {popup.message}
            </p>


            <button
              onClick={() => setPopup(false)}
              className="
            mt-8
            border
            border-[#2b2925]
            px-8
            py-3
            text-xs
            uppercase
            tracking-[0.3em]
            text-[#2b2925]
            hover:bg-[#2b2925]
            hover:text-white
            transition
          "
            >
              Close
            </button>


          </div>

        </>
      )}


    </>
  );
}